import type { InvalidResult } from '@/backend/errors/ValidationError';

export function printTopLevelValidationError(
  error: InvalidResult,
  message: string | null = null,
  slicePath = 0,
  indentation = '',
  didPrintTypeName = false,
): string {
  const typeName = error.typeName;
  const typeNamePart = !didPrintTypeName && typeName ? `<${typeName}>.` : '';

  return (
    typeNamePart +
    printValidationError(error, null, undefined, undefined, !!typeNamePart)
  );
}

/**
 * Pretty prints a validation error for logging in a console.
 *
 * @param error The error to print
 * @param message A message to substitute
 * @param slicePath Used internally to format the path property of the error
 * @param indentation Used internally to format child-errors found in the details property
 * @returns a string
 *
 * @example
 * `
 * An error
 *   foo: Should be a string
 *     Expected a thing got something else
 * `
 */
export function printValidationError(
  error: InvalidResult,
  message: string | null = null,
  slicePath = 0,
  indentation = '',
  didPrintTypeName = false,
): string {
  if (error.inner?.length === 1) {
    return printValidationError(error.inner[0], null, slicePath, indentation);
  }

  const path = error.path.slice(slicePath);
  slicePath = slicePath + path.length;

  const typeName = error.typeName;
  const typeNamePart = !didPrintTypeName && typeName ? `<${typeName}>` : '';

  const pathPart = path.length ? path.join('.') + ': ' : '';
  return `${indentation}${pathPart}[${error.problemCode}${typeNamePart}] ${
    message || error.description
  }\n${printValidationErrorDetails(error, slicePath, indentation + '  ', didPrintTypeName || !!typeNamePart)}`;
}

function printValidationErrorDetails(
  error: InvalidResult,
  slicePath: number,
  indentation: string,
  didPrintTypeName: boolean,
) {
  if (Array.isArray(error.details)) {
    return error.details
      .map((a) =>
        printValidationError(a, null, slicePath, indentation, didPrintTypeName),
      )
      .join('\n');
  }

  return `${indentation}Expected ${error.details.expected} got ${error.details.actual}`;
}
